import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserPlus, Loader2, Check, User, Mail, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const inviteSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters"),
  lastName: z
    .string()
    .max(50, "Last name must be less than 50 characters")
    .optional()
    .or(z.literal("")),
  email: z
    .string()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  phone: z
    .string()
    .max(20, "Phone number must be less than 20 characters")
    .optional()
    .or(z.literal("")),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface SearchResult {
  user_id: string;
  email: string;
  display_name: string | null;
}

interface InviteTeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  teamName: string;
  challengeId: string;
  isTeamLead: boolean;
  onInviteSent: () => void;
}

const InviteTeamMemberDialog: React.FC<InviteTeamMemberDialogProps> = ({
  open,
  onOpenChange,
  teamId,
  teamName,
  challengeId,
  isTeamLead,
  onInviteSent,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  // Debounced search for users
  useEffect(() => {
    if (searchTerm.length < 2 || selectedUser) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { data, error } = await supabase.rpc("search_users_for_invite", {
          search_term: searchTerm,
          limit_count: 5,
        });

        if (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } else {
          setSearchResults(data || []);
          setShowDropdown((data || []).length > 0);
        }
      } catch (err) {
        console.error("Search failed:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedUser]);

  const handleSelectUser = useCallback(
    (user: SearchResult) => {
      setSelectedUser(user);
      setSearchResults([]);
      setShowDropdown(false);
      setSearchTerm("");

      // Parse display_name into first/last name
      const nameParts = (user.display_name || "").trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Update form values
      form.setValue("firstName", firstName);
      form.setValue("lastName", lastName);
      form.setValue("email", user.email);
    },
    [form]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedUser(null);
    form.setValue("firstName", "");
    form.setValue("lastName", "");
    form.setValue("email", "");
    setSearchTerm("");
  }, [form]);

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      if (!selectedUser) {
        form.setValue("firstName", value);
      }
    },
    [form, selectedUser]
  );

  const handleAddNewPerson = useCallback(() => {
    // Keep the current search term as first name and dismiss dropdown
    // Don't clear searchTerm - keep it so user can continue typing
    setShowDropdown(false);
    setSearchResults([]);
    form.setValue("firstName", searchTerm);
  }, [searchTerm, form]);

  const onSubmit = async (data: InviteFormData) => {
    setIsSubmitting(true);

    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast.error("You must be logged in to invite team members");
        return;
      }

      // Construct full name for backward compatibility
      const fullName = data.lastName 
        ? `${data.firstName} ${data.lastName}`.trim() 
        : data.firstName;

      // Create the invitation
      const { data: invitation, error: inviteError } = await supabase
        .from("team_member_invitations")
        .insert({
          team_id: teamId,
          challenge_id: challengeId,
          invited_by: userData.user.id,
          invitee_name: fullName,
          invitee_first_name: data.firstName,
          invitee_last_name: data.lastName || null,
          invitee_email: data.email,
          invitee_phone: data.phone || null,
          invitee_user_id: selectedUser?.user_id || null,
          status: isTeamLead ? "pending" : "pending_approval",
          approval_required: !isTeamLead,
        })
        .select()
        .single();

      if (inviteError) throw inviteError;

      // If team lead, send the email immediately
      if (isTeamLead && invitation) {
        try {
          const { error: emailError } = await supabase.functions.invoke(
            "send-team-invite",
            {
              body: {
                invitation_id: invitation.id,
              },
            }
          );

          if (emailError) {
            console.error("Failed to send email:", emailError);
          }
        } catch (emailErr) {
          console.error("Email sending failed:", emailErr);
        }
      }

      toast.success(
        isTeamLead
          ? `Invitation sent to ${data.email}`
          : `Invitation submitted for team lead approval`
      );

      form.reset();
      setSelectedUser(null);
      setSearchTerm("");
      onOpenChange(false);
      onInviteSent();
    } catch (error: any) {
      console.error("Failed to create invitation:", error);
      toast.error(error.message || "Failed to send invitation");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setSearchTerm("");
      setSearchResults([]);
      setShowDropdown(false);
      form.reset();
    }
  }, [open, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite Team Member
          </DialogTitle>
          <DialogDescription>
            {isTeamLead
              ? `Invite someone to join "${teamName}". They'll receive an email invitation.`
              : `Submit an invitation request to join "${teamName}". The team lead will review and approve it.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Selected User Badge */}
            {selectedUser && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <User className="h-4 w-4 text-primary" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {selectedUser.display_name || selectedUser.email}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedUser.email}
                  </p>
                </div>
                <Badge variant="secondary" className="text-xs">
                  Existing User
                </Badge>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleClearSelection}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* First Name Field with Autocomplete */}
            {!selectedUser && (
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="relative">
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Start typing a name or email..."
                          value={searchTerm}
                          onChange={(e) =>
                            handleSearchInputChange(e.target.value)
                          }
                          onBlur={() => {
                            // Small delay to allow clicking dropdown items
                            setTimeout(() => setShowDropdown(false), 200);
                          }}
                          onFocus={() => {
                            // Sync searchTerm with field value if empty
                            if (!searchTerm && field.value) {
                              setSearchTerm(field.value);
                            }
                            if (searchResults.length > 0) {
                              setShowDropdown(true);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setShowDropdown(false);
                            }
                          }}
                          autoComplete="off"
                        />
                        {isSearching && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />

                    {/* Search Results Dropdown */}
                    {showDropdown && searchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md">
                        <Command>
                          <CommandList>
                            <CommandGroup heading="Existing Users">
                              {searchResults.map((user) => (
                                <CommandItem
                                  key={user.user_id}
                                  onSelect={() => handleSelectUser(user)}
                                  className="cursor-pointer"
                                >
                                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">
                                      {user.display_name || "No name"}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {user.email}
                                    </p>
                                  </div>
                                  <Check className="h-4 w-4 opacity-0 group-aria-selected:opacity-100" />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            
                            {/* Add New Person Option */}
                            <CommandGroup>
                              <CommandItem
                                onSelect={handleAddNewPerson}
                                className="cursor-pointer border-t"
                              >
                                <UserPlus className="mr-2 h-4 w-4 text-muted-foreground" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">
                                    Invite "{searchTerm}" as new person
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Not in the list? Add them manually
                                  </p>
                                </div>
                              </CommandItem>
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </div>
                    )}

                    {/* New User Hint */}
                    {searchTerm.length >= 2 &&
                      !isSearching &&
                      searchResults.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <Mail className="inline h-3 w-3 mr-1" />
                          No existing user found. They'll receive an email
                          invitation.
                        </p>
                      )}
                  </FormItem>
                )}
              />
            )}

            {/* Last Name Field */}
            {!selectedUser && (
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Optional"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Email Field - only show for new users */}
            {!selectedUser && (
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : isTeamLead ? (
                  "Send Invitation"
                ) : (
                  "Submit for Approval"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteTeamMemberDialog;
